import React, { useEffect, useRef, useState } from "react";
import httpClient from "../../../../../../api/httpClient";
import Cookies from "js-cookie";
import notify from "../../../../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import { Check, Search, X } from "lucide-react";

// Partial-search + save-new widget. Props mirror the one in CreateShipmentTab so the
// selection side-effects (locationERPIdentifier, lat/lng, locationRef) are preserved.
const LocationLookup = ({ stop, officeCode, onChange }) => {
  const [matches, setMatches] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const skipNextSearch = useRef(false);
  const blurTimer = useRef(null);

  useEffect(() => {
    if (skipNextSearch.current) {
      skipNextSearch.current = false;
      return undefined;
    }
    const query = stop.stopName?.trim();
    if (!query || query.length < 2) {
      setMatches([]);
      setOpen(false);
      return undefined;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await httpClient.get("/api/locations/partial-search", {
          params: { query, limit: 8 },
          headers: { "Office-Code": officeCode },
        });
        setMatches(data || []);
        setOpen(true);
      } catch {
        setMatches([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [stop.stopName, officeCode]);

  useEffect(() => () => clearTimeout(blurTimer.current), []);

  const selectLocation = (location) => {
    skipNextSearch.current = true;
    onChange({
      stopName: location.locationName || stop.stopName,
      locationERPIdentifier: location.customLocationIdentifier || "",
      addressLine1: location.addressLine1 || "",
      addressLine2: location.addressLine2 || "",
      city: location.city || "",
      state: location.state || "",
      zipCode: location.zipCode || "",
      country: location.country || "US",
      countryISO_CODE: (location.countryISO_CODE || location.country || "US").toUpperCase(),
      warehouseName: location.locationName || stop.stopName,
      locationRef: location.locationRef || "",
      latitude: location.latitude || null,
      longitude: location.longitude || null,
    });
    setOpen(false);
  };

  const saveLocation = async () => {
    if (!stop.stopName?.trim()) {
      notify.error({ title: "Location name needed", message: "Enter a location name before saving it for future use." });
      return;
    }
    setSaving(true);
    try {
      const { data } = await httpClient.post("/api/locations/add", {
        locationName: stop.stopName,
        customLocationIdentifier: stop.locationERPIdentifier || null,
        addressLine1: stop.addressLine1 || null,
        addressLine2: stop.addressLine2 || null,
        city: stop.city || null,
        state: stop.state || null,
        zipCode: stop.zipCode || null,
        country: stop.country || "US",
        officeCode,
        locationType: "POINT",
      }, { headers: { "Office-Code": officeCode } });
      selectLocation(data);
      notify.success({ title: "Location saved", message: `${data.locationName} is now available for future shipments.` });
    } catch (error) {
      notify.error({ title: "Location could not be saved", message: error?.response?.data || error.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Location name *</label>
      <div className="relative">
        <input
          type="text"
          value={stop.stopName || ""}
          onFocus={() => { if (matches.length && stop.stopName?.trim().length >= 2) setOpen(true); }}
          onBlur={() => { blurTimer.current = setTimeout(() => setOpen(false), 150); }}
          onChange={(e) => {
            skipNextSearch.current = false;
            const clearAutofill = stop.locationRef
              ? { locationERPIdentifier: "", addressLine1: "", addressLine2: "", city: "", state: "", zipCode: "", country: "US", countryISO_CODE: "US", warehouseName: "", locationRef: "", latitude: null, longitude: null }
              : {};
            onChange({ ...clearAutofill, stopName: e.target.value });
          }}
          className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 pr-8 text-sm outline-none hover:border-blue-600 focus:border-blue-600 focus:ring-1 focus:ring-blue-500 dark:border-acs-dark-border dark:bg-acs-dark-surface"
          placeholder="Search or type a location name"
        />
        <Search size={16} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#223688]" />
      </div>
      {(open || searching) && stop.stopName?.trim().length >= 2 && (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border border-[#D6E4FA] bg-white shadow-lg dark:border-acs-dark-border dark:bg-acs-dark-surface">
          {searching && <p className="px-3 py-2 text-sm text-gray-500">Searching locations…</p>}
          {!searching && matches.map((location) => {
            const meta = [
              [location.city, location.state, location.zipCode].filter(Boolean).join(", "),
              location.customLocationIdentifier || location.locationCode || "",
            ].filter(Boolean).join(" · ");
            return (
              <button
                key={location.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selectLocation(location)}
                className="block w-full border-b border-gray-100 px-3 py-2 text-left last:border-b-0 hover:bg-[#EDF5FF] dark:border-acs-dark-border dark:hover:bg-acs-dark-hover"
              >
                <span className="block text-sm font-semibold text-[#223688]">{location.locationName}</span>
                {meta ? <span className="block text-xs text-gray-500">{meta}</span> : null}
              </button>
            );
          })}
          {!searching && !matches.length && (
            <div className="p-3">
              <p className="text-sm text-gray-500">No matching location found. You can keep this name and enter the address below, or save it for future use.</p>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={saveLocation}
                disabled={saving}
                className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-[#223688] hover:text-[#17255F] disabled:opacity-50"
              >
                <Check size={15} /> {saving ? "Saving location…" : "Save as new location"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const EditEmptyStopModal = ({ stop, officeCode, onClose, onSaved }) => {
  const [draft, setDraft] = useState({ ...stop });
  const [saving, setSaving] = useState(false);

  const handleChange = (patch) => setDraft((prev) => ({ ...prev, ...patch }));

  const handleSave = async () => {
    if (!draft.stopName?.trim()) {
      notify.error({ title: "Location name required", message: "Enter or search a location name before saving." });
      return;
    }
    setSaving(true);
    try {
      const token = Cookies.get("authToken");
      await httpClient.patch(
        `/api/empty-stop/${stop.id}/location`,
        {
          stopName: draft.stopName,
          locationERPIdentifier: draft.locationERPIdentifier || null,
          addressLine1: draft.addressLine1 || null,
          addressLine2: draft.addressLine2 || null,
          city: draft.city || null,
          state: draft.state || null,
          zipCode: draft.zipCode || null,
          country: draft.country || "US",
          countryISO_CODE: draft.countryISO_CODE || "US",
          latitude: draft.latitude || null,
          longitude: draft.longitude || null,
          warehouseName: draft.warehouseName || null,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Office-Code": officeCode,
          },
        }
      );
      notify.success({
        title: "Location updated",
        message: "If charges depend on this location, use Rate Shopping to replan.",
      });
      onSaved();
      onClose();
    } catch (err) {
      if (err?.response?.status === 409) {
        notify.error({
          title: "Cannot edit",
          message: "This stop cannot be edited once the shipment has been tendered or the carrier has arrived.",
        });
      } else {
        notify.error({
          title: "Could not update location",
          message: err?.response?.data || err.message,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const label = stop.stopType === "EMPTY_PICKUP" ? "empty pickup" : "empty return";

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl dark:bg-acs-dark-surface">
        <div className="flex items-start justify-between border-b border-gray-200 dark:border-acs-dark-border px-5 py-4">
          <div>
            <h3 className="text-lg font-semibold text-[#030229] dark:text-acs-dark-text capitalize">
              Edit {label} location
            </h3>
            <p className="mt-0.5 text-sm text-gray-500">
              Search an existing location or type a name and enter the address below.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          <LocationLookup stop={draft} officeCode={officeCode} onChange={handleChange} />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Address Line 1</label>
              <input
                type="text"
                value={draft.addressLine1 || ""}
                onChange={(e) => handleChange({ addressLine1: e.target.value })}
                className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm dark:border-acs-dark-border dark:bg-acs-dark-surface"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Address Line 2</label>
              <input
                type="text"
                value={draft.addressLine2 || ""}
                onChange={(e) => handleChange({ addressLine2: e.target.value })}
                className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm dark:border-acs-dark-border dark:bg-acs-dark-surface"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">City</label>
              <input
                type="text"
                value={draft.city || ""}
                onChange={(e) => handleChange({ city: e.target.value })}
                className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm dark:border-acs-dark-border dark:bg-acs-dark-surface"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">State</label>
              <input
                type="text"
                value={draft.state || ""}
                onChange={(e) => handleChange({ state: e.target.value })}
                className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm dark:border-acs-dark-border dark:bg-acs-dark-surface"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Zip Code</label>
              <input
                type="text"
                value={draft.zipCode || ""}
                onChange={(e) => handleChange({ zipCode: e.target.value })}
                className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm dark:border-acs-dark-border dark:bg-acs-dark-surface"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Site / ERP ID</label>
              <input
                type="text"
                value={draft.locationERPIdentifier || ""}
                onChange={(e) => handleChange({ locationERPIdentifier: e.target.value })}
                className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm dark:border-acs-dark-border dark:bg-acs-dark-surface"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 dark:border-acs-dark-border px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !draft.stopName?.trim()}
            className="rounded-md bg-[#223688] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1a2d66] disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save location"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditEmptyStopModal;
